import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  GestureResponderEvent,
  Platform,
  Pressable,
  View,
  ViewStyle,
} from 'react-native';
import {observer} from 'mobx-react-lite';

import {AUD} from '../../constants/GlobalConstants';
import {useCartMutation} from '../../queries/cart/useCartMutation';
import {useStores} from '../../stores/use-stores';
import {OrderLine} from '../../transport/apiServiceResponse';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventSourceType} from '../../utils/analytics/events';
import {buildProductTestId} from '../../utils/string';
import {Icon} from '../Icon';
import {CART_BUTTON_DIMENSIONS, cartButtonStyles as styles, COLORS} from '../ProductTile/styles';
import Text from '../ui-controls/Text';

// Stop event propagation on web to prevent parent pressables from capturing clicks
const stopPropagation = (e: GestureResponderEvent) => {
  if (Platform.OS === 'web' && e?.nativeEvent) {
    // Access the underlying DOM event on web and stop it from bubbling to parent Pressables
    const domEvent = e.nativeEvent as unknown as {
      stopPropagation?: () => void;
      preventDefault?: () => void;
    };
    domEvent.stopPropagation?.();
    domEvent.preventDefault?.();
  }
};

// Web-only props to stop all click events from bubbling to parent Pressable
const webEventBlockers =
  Platform.OS === 'web'
    ? {
        onClick: (e: unknown) => {
          const event = e as {stopPropagation?: () => void};
          event.stopPropagation?.();
        },
        onClickCapture: (e: unknown) => {
          const event = e as {stopPropagation?: () => void};
          event.stopPropagation?.();
        },
      }
    : {};

type CartButtonProps = {
  item: OrderLine;
  size?: number;
  innerStyle?: ViewStyle;
  onAddItem?: () => void;
  disabled?: boolean;
  testContext?: string;
};

const CartButton = observer(
  ({
    size = CART_BUTTON_DIMENSIONS.iconSize,
    innerStyle,
    item,
    onAddItem,
    disabled = false,
    testContext,
  }: CartButtonProps) => {
    const {shopSelectionStore, cartStore} = useStores();
    const {mutate: updateCart, isPending} = useCartMutation();

    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);

    const productTestId = buildProductTestId(item.name, testContext);

    // Sync count from cart store when changed on another screen.
    // Skip while mutation is in-flight to prevent stale cart state
    // from resetting the quantity stepper mid-update.
    useEffect(() => {
      if (isPending) return;
      const qty = cartStore.getItemQty(item.product_id);
      if (qty >= 1) {
        setCount(qty);
        setOpen(true);
      } else {
        setCount(qty);
        setOpen(false);
      }
    }, [cartStore, item.product_id, cartStore.currentOrder, isPending]);

    const addItem = useCallback(
      (e?: GestureResponderEvent) => {
        if (e) {
          stopPropagation(e);
        }

        if (isPending) {
          return null;
        }

        if (onAddItem) {
          onAddItem();
        } else {
          const newCount = count + 1;

          updateCart({productId: item.product_id, quantity: newCount});
          setCount(newCount);
          // Don't set open=true optimistically - let useEffect handle it after
          // the mutation succeeds. This keeps the ADD TO CART button visible
          // with a loading spinner until the API completes.

          Analytics.logEvent({
            eventName: EventName.AddToCart,
            parameters: {
              source_type: EventSourceType.Button,
              value: item.sell_price_cents / 100,
              currency: AUD,
              items: [
                {
                  item_id: item.product_id,
                  item_name: item.name,
                  item_brand: item.brand_name,
                  price: item.sell_price_cents / 100,
                  quantity: newCount,
                  discount: (item.normal_price_cents - item.sell_price_cents) / 100,
                },
              ],
            },
          });
        }
      },
      [isPending, count, item, onAddItem, updateCart],
    );

    const removeItem = useCallback(
      (e?: GestureResponderEvent) => {
        if (e) {
          stopPropagation(e);
        }

        if (isPending) {
          return null;
        }
        const countNew = count - 1;
        if (countNew <= 0) {
          setCount(0);
          setOpen(false);
        } else {
          setCount(countNew);
        }
        updateCart({productId: item.product_id, quantity: countNew});
      },
      [isPending, count, item.product_id, updateCart],
    );

    if (shopSelectionStore.ecommerceDisabled) {
      return (
        <Pressable onPress={addItem} disabled>
          <View style={styles.disabledButton}>
            <Text style={styles.shopInstoreText}>SHOP</Text>
            <Text style={styles.shopInstoreText}>INSTORE</Text>
          </View>
        </Pressable>
      );
    }

    if (open) {
      return (
        <View
          style={styles.quantityContainer}
          // Capture pointer events to prevent parent pressable from receiving them
          onStartShouldSetResponder={() => true}
          onResponderTerminationRequest={() => false}
          // @ts-expect-error - onClick/onClickCapture are valid on web but not typed in RN
          {...webEventBlockers}>
          <Pressable
            testID={`${productTestId}-decrement-quantity`}
            onPress={removeItem}
            disabled={isPending}>
            {({pressed}) => (
              <View style={[styles.quantityButton, pressed && styles.quantityButtonPressed]}>
                {isPending ? (
                  <ActivityIndicator size="small" color={COLORS.buttonIcon} />
                ) : (
                  <Icon name="minus" size={size} color={COLORS.buttonIcon} />
                )}
              </View>
            )}
          </Pressable>
          <View style={styles.quantityDisplay}>
            <Text style={styles.quantityText}>{count}</Text>
          </View>
          <Pressable
            testID={`${productTestId}-increment-quantity`}
            onPress={addItem}
            disabled={isPending}>
            {({pressed}) => (
              <View style={[styles.quantityButton, pressed && styles.quantityButtonPressed]}>
                {isPending ? (
                  <ActivityIndicator size="small" color={COLORS.buttonIcon} />
                ) : (
                  <Icon name="plus" size={size} color={COLORS.buttonIcon} />
                )}
              </View>
            )}
          </Pressable>
        </View>
      );
    }

    return (
      <View
        // Capture pointer events to prevent parent pressable from receiving them
        onStartShouldSetResponder={() => true}
        onResponderTerminationRequest={() => false}
        // @ts-expect-error - onClick/onClickCapture are valid on web but not typed in RN
        {...webEventBlockers}>
        <Pressable
          disabled={disabled || isPending}
          onPress={addItem}
          accessibilityLabel={`Add ${item.name} to cart`}
          testID={`${productTestId}-add-button`}>
          <View style={[styles.addToCartButton, innerStyle]}>
            {isPending ? (
              <ActivityIndicator
                size="small"
                color={COLORS.buttonIcon}
                style={styles.addToCartLoadingOverlay}
              />
            ) : null}
            <Text style={[styles.addToCartText, isPending && styles.addToCartTextLoading]}>
              ADD TO CART
            </Text>
          </View>
        </Pressable>
      </View>
    );
  },
);

export default CartButton;
